import * as React from "react"
import { Eye, Info, Pencil } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ListEditorSheet } from "@/components/alfred/list-editor-sheet"
import {
  MaskedCredentialInput,
  PROTECTED_CREDENTIAL_MASK,
} from "@/components/shared/masked-credential-input"
import type { ProVenueInstanceInput } from "@/types/alfred-config"

interface ProVenueInstanceEditorProps {
  items: ProVenueInstanceInput[]
  onChange: (next: ProVenueInstanceInput[]) => void
  disabled?: boolean
  triggerAriaLabel?: string
  /** The displayed rows are resolved global metadata and remain read-only. */
  usesGlobalDefault?: boolean
  /**
   * When true, every instance must carry freshly entered credentials (used for
   * bulk replacement, where secrets cannot be copied from the selected
   * devices). When false, only newly added instances require credentials and
   * blank inputs on existing instances mean "keep current".
   */
  requireSecret?: boolean
}

const INPUT_CLASSES =
  "bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
const LABEL_CLASSES = "text-[13px] text-muted-foreground font-medium"

function emptyProVenueInstance(): ProVenueInstanceInput {
  return {
    name: "",
    hostName: "",
    consumerKey: "",
    consumerSecret: "",
  }
}

export function ProVenueInstanceEditor({
  items,
  onChange,
  disabled = false,
  triggerAriaLabel,
  usesGlobalDefault = false,
  requireSecret = false,
}: ProVenueInstanceEditorProps) {
  const [open, setOpen] = React.useState(false)
  const readOnly = disabled || usesGlobalDefault
  const allCredentialsRequired = requireSecret

  return (
    <>
      <Button
        aria-label={triggerAriaLabel}
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-card border-input text-foreground hover:bg-secondary"
      >
        {disabled ? (
          <Eye className="h-4 w-4 mr-2" />
        ) : (
          <Pencil className="h-4 w-4 mr-2" />
        )}
        {disabled ? "View" : "Manage"}
      </Button>
      {!disabled ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="About ProVenue instance inheritance"
            >
              <Info data-icon="inline-start" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-w-[calc(100vw-2rem)]">
            <p className="text-sm">
              {"Partial overrides aren't supported: use the complete global list or provide a complete device-specific list."}
            </p>
          </PopoverContent>
        </Popover>
      ) : null}
      <ListEditorSheet<ProVenueInstanceInput>
        open={open}
        onOpenChange={setOpen}
        title={usesGlobalDefault ? "Global ProVenue instances" : "ProVenue instances"}
        description={
          usesGlobalDefault
            ? "Inherited from Global Defaults. Clear Use default to create a complete device-specific replacement."
            : "Manage ProVenue instance connections used by Alfred."
        }
        itemNoun="instance"
        items={items}
        newItem={emptyProVenueInstance}
        readOnly={readOnly}
        getValidationError={(draft, newItemIndexes) => {
          const missingCredentials = draft.some((item, index) =>
            (allCredentialsRequired || newItemIndexes.has(index)) &&
            (!item.consumerKey?.trim() || !item.consumerSecret?.trim()),
          )
          if (!missingCredentials) return undefined
          return allCredentialsRequired
            ? "Enter a consumer key and consumer secret for every ProVenue instance."
            : "Enter a consumer key and consumer secret for every new ProVenue instance."
        }}
        renderForm={(item, update, _index, readOnly, isNew) => {
          const credentialsRequired = allCredentialsRequired || isNew
          return (
          <>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Name</Label>
              <Input
                value={item.name}
                disabled={readOnly}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="e.g. Primary"
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Host name</Label>
              <Input
                value={item.hostName}
                disabled={readOnly}
                onChange={(e) => update({ hostName: e.target.value })}
                placeholder="provenue.example.com"
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Consumer Key</Label>
              <MaskedCredentialInput
                aria-label="Consumer Key"
                value={readOnly ? PROTECTED_CREDENTIAL_MASK : item.consumerKey}
                disabled={readOnly}
                onChange={(e) => update({ consumerKey: e.target.value })}
                placeholder={
                  readOnly
                    ? undefined
                    : credentialsRequired
                      ? "Enter consumer key"
                      : "Leave blank to keep current"
                }
                required={!readOnly && credentialsRequired}
                aria-required={!readOnly && credentialsRequired}
                className={`${INPUT_CLASSES} placeholder:text-muted-foreground`}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Consumer Secret</Label>
              <MaskedCredentialInput
                aria-label="Consumer Secret"
                value={readOnly ? PROTECTED_CREDENTIAL_MASK : item.consumerSecret}
                disabled={readOnly}
                onChange={(e) => update({ consumerSecret: e.target.value })}
                placeholder={
                  readOnly
                    ? undefined
                    : credentialsRequired
                      ? "Enter consumer secret"
                      : "Leave blank to keep current"
                }
                required={!readOnly && credentialsRequired}
                aria-required={!readOnly && credentialsRequired}
                className={`${INPUT_CLASSES} placeholder:text-muted-foreground`}
              />
              {!readOnly && (
                  <p className="text-xs text-muted-foreground">
                    {credentialsRequired
                      ? "A consumer key and consumer secret are required for every new instance."
                      : "Leave blank to keep current. To change them, enter a new consumer key and consumer secret."}
                  </p>
              )}
            </div>
          </>
          )
        }}
        onSubmit={onChange}
      />
    </>
  )
}
